import { normalize } from 'normalizr'
import { Schemas } from '@redux/schema'
import { updateEntities, deleteEntity } from '@redux/entities'

import api from '@functions/api'

import type { PublicationBookingItemModel } from '@models/types'

import type { AppDispatch } from '@redux/store'
import type {
  Action, DeleteParams, EntityOptions, Errors, FetchRequestPayload, RootReducerState,
} from '@redux/modules/types'

import { handleError } from '../utils'

const FETCH_REQUEST = 'realbase/publicationBookingItem/FETCH_REQUEST'
const FETCH_SUCCESS = 'realbase/publicationBookingItem/FETCH_SUCCESS'
const FETCH_FAILURE = 'realbase/publicationBookingItem/FETCH_FAILURE'

const CREATE_REQUEST = 'realbase/publicationBookingItem/CREATE_REQUEST'
const CREATE_SUCCESS = 'realbase/publicationBookingItem/CREATE_SUCCESS'
const CREATE_FAILURE = 'realbase/publicationBookingItem/CREATE_FAILURE'

const UPDATE_REQUEST = 'realbase/publicationBookingItem/UPDATE_REQUEST'
const UPDATE_SUCCESS = 'realbase/publicationBookingItem/UPDATE_SUCCESS'
const UPDATE_FAILURE = 'realbase/publicationBookingItem/UPDATE_FAILURE'

const DELETE_REQUEST = 'realbase/publicationBookingItem/DELETE_REQUEST'
const DELETE_SUCCESS = 'realbase/publicationBookingItem/DELETE_SUCCESS'
const DELETE_FAILURE = 'realbase/publicationBookingItem/DELETE_FAILURE'

export type PublicationBookingItemModuleState = {
  creating: boolean,
  deleting: boolean,
  errors: string[],
  loaded: boolean,
  loadedForKeys: string[],
  loading: boolean,
  updating: boolean,
}

// Initial State
const initialState: PublicationBookingItemModuleState = {
  creating: false,
  deleting: false,
  errors: [],
  loaded: false,
  loadedForKeys: [],
  loading: false,
  updating: false,
}

// Actions
export function fetchRequest(payload: FetchRequestPayload = {}) {
  return {
    type: FETCH_REQUEST,
    loadedForKeys: payload.loadedForKeys,
  }
}

export function fetchSuccess() {
  return {
    type: FETCH_SUCCESS,
  }
}

export function fetchFailure(errors: Errors = []) {
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function createRequest() {
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess() {
  return {
    type: CREATE_SUCCESS,
  }
}

export function createFailure(errors: Errors = []) {
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

export function updateRequest() {
  return {
    type: UPDATE_REQUEST,
  }
}

export function updateSuccess() {
  return {
    type: UPDATE_SUCCESS,
  }
}

export function updateFailure(errors: Errors = []) {
  return {
    type: UPDATE_FAILURE,
    errors,
  }
}

export function deleteRequest() {
  return {
    type: DELETE_REQUEST,
  }
}

export function deleteSuccess() {
  return {
    type: DELETE_SUCCESS,
  }
}

export function deleteFailure(errors: Errors = []) {
  return {
    type: DELETE_FAILURE,
    errors,
  }
}

// Action Creators
export function loadPublicationBookingItems(options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const loadedForKeys = [...getState().publicationBookingItems.loadedForKeys]

    if (entityKey && !loadedForKeys.includes(entityKey)){
      loadedForKeys.push(entityKey)
    }

    dispatch(fetchRequest({ loadedForKeys }))

    const promise = api('/publication_booking_items.json', options)
      .then(({ data }: { data: PublicationBookingItemModel[] }) => {
        const normalizedJson = normalize(data, Schemas.PUBLICATION_BOOKING_ITEM_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function createPublicationBookingItem(
  publicationBookingItem: Partial<PublicationBookingItemModel>,
  options: EntityOptions = {},
) {
  const config = {
    method: 'POST',
    data: JSON.stringify({
      publication_booking_item: publicationBookingItem,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api('/publication_booking_items.json', options, config)
      .then(({ data }: { data: PublicationBookingItemModel }) => {
        const normalizedJson = normalize(data, Schemas.PUBLICATION_BOOKING_ITEM)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function updatePublicationBookingItem(
  publicationBookingItem: Partial<PublicationBookingItemModel>,
  options: EntityOptions = {},
) {
  const config = {
    method: 'PUT',
    data: JSON.stringify({
      publication_booking_item: publicationBookingItem,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/publication_booking_items/${publicationBookingItem.id}.json`, options, config)
      .then(({ data }: { data: PublicationBookingItemModel }) => {
        const normalizedJson = normalize(data, Schemas.PUBLICATION_BOOKING_ITEM)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function cancelPublicationBookingItem(
  publicationBookingItem: Partial<PublicationBookingItemModel>,
  options: EntityOptions = {},
) {
  const config = {
    method: 'PUT',
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/publication_booking_items/${publicationBookingItem.id}/cancel.json`, options, config)
      .then(({ data }: { data: PublicationBookingItemModel }) => {
        const normalizedJson = normalize(data, Schemas.PUBLICATION_BOOKING_ITEM)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function removePublicationBookingItem(
  publicationBookingItem: Partial<PublicationBookingItemModel>,
  options: EntityOptions = {},
) {
  const config = {
    method: 'PUT',
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/publication_booking_items/${publicationBookingItem.id}/remove.json`, options, config)
      .then(({ data }: { data: PublicationBookingItemModel }) => {
        const normalizedJson = normalize(data, Schemas.PUBLICATION_BOOKING_ITEM)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function deletePublicationBookingItem(publicationBookingItem: DeleteParams<PublicationBookingItemModel>) {
  const config = {
    method: 'DELETE',
  }

  return (dispatch: AppDispatch) => {
    dispatch(deleteRequest())

    const promise = api(`/publication_booking_items/${publicationBookingItem.id}.json`, {}, config)
      .then(({ data }: { data: { id: number } }) => {
        const normalizedJson = normalize(data, Schemas.PUBLICATION_BOOKING_ITEM)
        dispatch(deleteEntity(normalizedJson))
        dispatch(deleteSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(deleteFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function bulkDeletePublicationBookingItems(publicationBookingItemIds: number[]) {
  const config = {
    method: 'DELETE',
    data: JSON.stringify({
      publication_booking_item_ids: publicationBookingItemIds,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(deleteRequest())

    const promise = api('/publication_booking_items/bulk_delete.json', {}, config)
      .then(({ data }: { data: { ids: number[] } }) => {
        if (data.ids){
          data.ids.forEach((id) => {
            const normalizedJson = normalize({ id }, Schemas.PUBLICATION_BOOKING_ITEM)
            dispatch(deleteEntity(normalizedJson))
          })
        }

        dispatch(deleteSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(deleteFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function removePublicationBookingItemFromStore(publicationBookingItemId: number) {
  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const state = getState()
    const { publicationBookingItems } = state.entities

    const publicationBookingItem = publicationBookingItems[publicationBookingItemId]

    if (publicationBookingItem){
      const normalizedJson = normalize(publicationBookingItem, Schemas.PUBLICATION_BOOKING_ITEM)
      dispatch(deleteEntity(normalizedJson))
      dispatch(deleteSuccess())
    }
  }
}

// Reducer
export default function reducer(
  state: PublicationBookingItemModuleState = initialState,
  action: Action = { type: '' },
): PublicationBookingItemModuleState {
  switch (action.type){
    case FETCH_REQUEST:
      return {
        ...state,
        loadedForKeys: action.loadedForKeys || state.loadedForKeys,
        loading: true,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        errors: [],
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors: action.errors || [],
      }
    case CREATE_REQUEST:
      return {
        ...state,
        creating: true,
      }
    case CREATE_SUCCESS:
      return {
        ...state,
        creating: false,
        errors: [],
      }
    case CREATE_FAILURE:
      return {
        ...state,
        creating: false,
        errors: action.errors || [],
      }
    case UPDATE_REQUEST:
      return {
        ...state,
        updating: true,
      }
    case UPDATE_SUCCESS:
      return {
        ...state,
        updating: false,
        errors: [],
      }
    case UPDATE_FAILURE:
      return {
        ...state,
        updating: false,
        errors: action.errors || [],
      }
    case DELETE_REQUEST:
      return {
        ...state,
        deleting: true,
      }
    case DELETE_SUCCESS:
      return {
        ...state,
        deleting: false,
        errors: [],
      }
    case DELETE_FAILURE:
      return {
        ...state,
        deleting: false,
        errors: action.errors || [],
      }
    default:
      return state
  }
}
